/**
 * Shared time and duration formatting utilities
 */

/**
 * Formats a duration in milliseconds to a human-readable string
 * @param duration - Duration in milliseconds
 * @returns Formatted duration string (e.g., "1.5s", "500ms")
 */
export const formatDurationShort = (duration: number): string => {
  if (duration < 1000) return `${duration}ms`;
  return `${(duration / 1000).toFixed(1)}s`;
};

/**
 * Formats a duration in milliseconds to a longer human-readable string
 * @param duration - Duration in milliseconds
 * @returns Formatted duration string (e.g., "just now", "5 seconds", "1 minute")
 */
export const formatDurationLong = (duration: number): string => {
  if (duration < 1000) return "just now";
  if (duration < 2000) return "1 second";
  if (duration < 60000) return `${Math.round(duration / 1000)} seconds`;
  if (duration < 120000) return "1 minute";
  return `${Math.round(duration / 60000)} minutes`;
};

/**
 * Formats how long ago something happened, at the coarse "2m ago" grain a
 * save-status line uses (SE-8389, PRD §3.4 "Saved 2m ago").
 * @param elapsedMs - Milliseconds since the event; negative reads as just now
 * @returns "just now" under a minute, then "{n}m ago", then "{n}h ago"
 */
export const formatElapsedAgo = (elapsedMs: number): string => {
  const minutes = Math.floor(Math.max(elapsedMs, 0) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
};

/**
 * Formats a duration in seconds to a human-readable string
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "1h 30m", "45s")
 */
export const formatDurationFromSeconds = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};
